/* ==========================================================================
   FILE: App.jsx
   IAMONEAI - Main Application Component
   Root component with routing and providers
   ========================================================================== */

import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from './global/context/ThemeContext';
import { AppProvider, useApp } from './global/context/AppContext';

// Pages
import HomePage from './pages/HomePage';
import AuthPage from './pages/AuthPage';
import DashboardPage from './pages/DashboardPage';

// Global styles
import './global/styles/index.css';

// ==========================================================================
// PROTECTED ROUTE COMPONENT
// ==========================================================================

/**
 * Wrapper for routes that require authentication
 */
function ProtectedRoute({ children }) {
  const { user, isLoading } = useApp();

  if (isLoading) {
    return (
      <div className="app-loading">
        <div className="app-loading-spinner" />
        <span>Loading...</span>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth?mode=login" replace />;
  }

  return children;
}

// ==========================================================================
// PUBLIC ROUTE COMPONENT
// ==========================================================================

/**
 * Wrapper for routes that should redirect to dashboard if logged in
 */
function PublicRoute({ children }) {
  const { user, isLoading } = useApp();

  if (isLoading) {
    return (
      <div className="app-loading">
        <div className="app-loading-spinner" />
        <span>Loading...</span>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

// ==========================================================================
// APP ROUTES
// ==========================================================================

function AppRoutes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route 
        path="/" 
        element={
          <PublicRoute>
            <HomePage />
          </PublicRoute>
        } 
      />
      <Route 
        path="/auth" 
        element={
          <PublicRoute>
            <AuthPage />
          </PublicRoute>
        } 
      />

      {/* Protected routes */}
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        } 
      />

      {/* Catch all - redirect to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

// ==========================================================================
// MAIN APP COMPONENT
// ==========================================================================

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AppProvider>
          <div className="app">
            <AppRoutes />
          </div>
        </AppProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

// ==========================================================================
// APP STYLES (inline for loading states)
// ==========================================================================

const appStyles = `
.app {
  min-height: 100vh;
  background-color: var(--bg-primary);
}

.app-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  gap: var(--space-3);
  background-color: var(--bg-primary);
  color: var(--text-secondary);
}

.app-loading-spinner {
  width: 48px;
  height: 48px;
  border: 3px solid var(--border-default);
  border-top-color: var(--color-primary);
  border-radius: 50%;
  animation: app-spin 1s linear infinite;
}

@keyframes app-spin {
  to {
    transform: rotate(360deg);
  }
}
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleEl = document.createElement('style');
  styleEl.textContent = appStyles;
  document.head.appendChild(styleEl);
}

// ==========================================================================
// EXPORTS
// ==========================================================================

export default App;